import { useQuery } from 'react-query';
import { CMAType } from '../../../../AdviserSettings/Page/CustomModelAssumptions/types';
import { OptionType } from '../../../../CommonComponents/OPL/Select/BaseSelect/types';
import { authFetch } from '../../../../CommonUtils/authFetch';
import { ManualAccountsAssetAllocationDTO } from '../../../../ManualAccountsAssetAllocation/types';
import { queryKeys } from './queryKeys';
import {
  AccountDistributionStrategyIdDTO,
  CurrentDistributionOrderDTO,
} from '../../../../AccountDistributionOrderModal/api/useAccountDistributionOptionsQuery';

export interface RetirementExpenseOption {
  value: number;
  label: string;
}

export interface NetInvestmentIncomeTaxOption {
  value: number;
  label: string;
  description: string;
}

export interface AssumptionsDTO {
  assumeCurrentTaxRateProvisionsExpire: boolean | null;
  capitalGainsRatio: number | null;
  incomeGrowthRate: number | null;
  inflationRate: number | null;
  lifeExpectancy: number | null;
  loanBalanceInterestRate: number | null;
  monteCarloSimulations: number | null;
  portfolioReturnsMethodology: number | null;
  potentialRevenueRate: number | null;
  socialSecurityInflationRate: number | null;
  tuitionInflationRate: number | null;
  turnoverRatio: number | null;
  riskToleranceWorkflow: number | null;
  enableAutoGeneratedWhatIfs: boolean;
  capitalMarketAssumptions: CMAType;
  enhancedReportExperience: boolean;
  excessCashFlowToSave: number | null;
  manualAccountsAssetAllocation: ManualAccountsAssetAllocationDTO;
  monteCarloSuccessRange: number | null;
  retirementExpenses: number | null;
  retirementExpensesOptions: RetirementExpenseOption[];
  retirementExpensesAmount: number | null;
  accountDistributionStrategy: AccountDistributionStrategyIdDTO;
  imposeNetInvestmentIncomeTax: number | null;
  netInvestmentIncomeTaxOptions: NetInvestmentIncomeTaxOption[];
}

export interface AgreementDTO {
  id: string;
  name: string;
  file: string;
  isActive: boolean;
  lastUpdated: string;
  fileSize: number;
}

export interface FirmSettingsDTO {
  agreements: AgreementDTO[];
  allowAdvisersOverrideAgreements: boolean;
  allowAdvisersCustomTermsAndConditions: boolean;
  allowAdvisersOverrideFirmLogo: boolean;
  assumptionsEditableByAdviser: boolean;
  clientLandingPage: number | null;
  clientLandingPageOptions: OptionType<number>[];
  customDisclosures: string;
  customDisclosuresEditableByAdviser: boolean;
  customFirmUrl: string;
  customTermsAndConditions: string;
  disableAdviserSuggestions: boolean;
  firmAssumptions: AssumptionsDTO;
  firmLogoFilename: string | null;
  supportTeamEmails: string[] | null;
  allowAssetBucketing: boolean;
  allowClientToCustomizeAssetAssignmentAcrossBuckets: boolean;
  displayAssetBucketing: boolean;
  showAgreements: boolean;
  is3dRiskToleranceWorkflowEnabled: boolean;
  allowAdviserOverrideAddManualAccount: boolean;
  allowAddManualAccount: boolean;
  allowBeFi20: boolean;
  hasAcceptedBeFi20Terms: boolean;
  allowAdvisersToDelegateClientLevelAssetBucketingCustomization: boolean;
  allowAdvisersToCustomizeAssetBucketingLayout: boolean;
  allowPulseCheck: boolean;
  allowMobileAppPushNotifications: boolean;
  defaultDocumentUploadMobilePushNotifications: boolean;
  defaultNewsfeedMobilePushNotifications: boolean;
  allowPersonalFinancesCsvExport: boolean;
  hasDocumentVault: boolean;
  hasNewsfeed: boolean;
  allowAccountNickname: boolean;
  accountDistributionOrder: CurrentDistributionOrderDTO;
  pdfImportEnabled: boolean;
  allowAiPdfReader: boolean;
  hasAcceptedAiPdfReaderTerms: boolean;
}

const fetchFirmSettings = async (): Promise<FirmSettingsDTO> => authFetch('/Adviser/Settings/Firm');

export const useFirmSettingsQuery = () => {
  return useQuery(
    queryKeys.firmSettings(),

    () => fetchFirmSettings(),

    {
      cacheTime: 0,
    },
  );
};
